import { useCallback, useState } from 'react';
import type { SerializedAnnotation } from '@annoteer/annotation-engine';
import type { DocumentInfo } from '../types/document.js';
import { useSync } from '../contexts/SyncContext.js';
import { annotatedExportName } from '../utils/export-name.js';

/**
 * Download the open document with its annotations burned in.
 *
 * Only formats rendered through the annotation overlay are exportable: PDF,
 * and presentations, which have already been converted to PDF server-side by
 * the time they reach the viewer — so both produce a PDF. DOCX and XLSX are
 * edited natively and have their own Export buttons; they carry no overlay and
 * are deliberately excluded here.
 */
/**
 * Record how the last export ended, where the state readout can show it.
 *
 * localStorage, so it survives the WebView teardown that Teams for Android
 * performs when an external activity takes the foreground -- a download is one.
 * Without this, "the button does nothing" and "the export ran and the download
 * was swallowed" are indistinguishable, which is the trap that cost two days on
 * the file picker.
 */
function noteExport(outcome: string): void {
  try {
    window.localStorage.setItem('annoteer.lastExport', outcome);
  } catch {
    // diagnostics must never break an export
  }
}

export function useAnnotatedExport(documentInfo: DocumentInfo | null) {
  const { syncDoc } = useSync();
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  /** Name of the last file handed to the browser, so the UI can confirm it. */
  const [exportedName, setExportedName] = useState<string | null>(null);

  const canExport =
    documentInfo != null &&
    (documentInfo.documentType === 'pdf' || documentInfo.documentType === 'presentation');

  const exportAnnotated = useCallback(async () => {
    if (!documentInfo || !canExport) return;

    setIsExporting(true);
    setExportError(null);
    setExportedName(null);
    noteExport('start pdf');
    try {
      // Lazy so pdf-lib is only fetched when somebody actually exports.
      const { exportAnnotatedPdf } = await import('@annoteer/annotation-engine');

      // Yjs holds the annotations already in document coordinates, which is
      // exactly what the PDF writer wants — no scale conversion here.
      const annotations = syncDoc
        ? (Array.from(syncDoc.getAnnotations().values()) as unknown as SerializedAnnotation[])
        : [];

      const bytes = await exportAnnotatedPdf(documentInfo.data, annotations);

      // Copy into a fresh buffer: `bytes` may be a view over a larger
      // ArrayBuffer, and Blob would otherwise capture the whole thing.
      const blob = new Blob([new Uint8Array(bytes)], { type: 'application/pdf' });
      noteExport(`built pdf ${bytes.length}b`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const downloadName = annotatedExportName(documentInfo.fileName, 'pdf');
      a.download = downloadName;
      a.click();
      URL.revokeObjectURL(url);
      // The click is where a mobile WebView can silently drop the download: the
      // blob is built and valid, and nothing is saved. Recorded separately from
      // "built" so the two can be told apart.
      noteExport(`clicked ${downloadName} ${bytes.length}b`);
      setExportedName(downloadName);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Export failed';
      noteExport('FAILED ' + message);
      console.error('Annotated export failed:', err);
      setExportError(message);
    } finally {
      setIsExporting(false);
    }
  }, [documentInfo, canExport, syncDoc]);

  return { canExport, isExporting, exportError, exportedName, exportAnnotated };
}

/** Strip a trailing extension; a presentation exports as .pdf, not .pptx.pdf. */
